import { useState, useEffect, useCallback, useRef } from 'react';
import { PermissionStatus } from 'react-native-permissions';

/**
 * Live dictation in the browser, through the Web Speech API.
 *
 * The recogniser was always asked for interim results — `interimResults = true`
 * below has been set since this hook was written — but `onresult` kept only the
 * final ones and threw the rest away, so nothing ever appeared until the
 * technician stopped talking. That discarded text IS live transcription; this
 * hook now keeps it.
 *
 * `recognizedText` is therefore two things joined: the finals, which no longer
 * change, and the one interim the recogniser is still revising. A caller shows
 * the whole string and it self-corrects as the words settle, the way dictation
 * does elsewhere.
 *
 * Two behaviours of the platform recogniser that callers must not have to know:
 *
 *   * **`continuous` does not mean continuous.** Chrome ends the session after
 *     a stretch of silence and fires `onend` regardless. Left alone, the mic
 *     goes deaf mid-sentence while the UI still says it is listening, so a
 *     session the caller has not stopped is restarted here.
 *   * **It is not everywhere.** Firefox has no Web Speech API at all, and a
 *     caller needs to know that before it offers the feature rather than after
 *     `start()` throws. Hence `isSupported`.
 *
 * What this cannot do is worth stating where somebody will read it: the
 * recogniser cannot be taught a vocabulary. Part numbers, asset numbers and
 * repair names are transcribed by a general model, and audio leaves the device
 * — Google's servers in Chrome, Apple's in Safari.
 */
export function useSpeechToText(onTextReceived: (text: string) => void) {
  const [isListening, setIsListening] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  const onTextReceivedRef = useRef(onTextReceived);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  // What the caller asked for, as opposed to what the recogniser is doing. The
  // two disagree every time Chrome ends a session on silence.
  const wantListeningRef = useRef(false);
  // Finals accumulate; the interim is replaced each time it is revised.
  const finalRef = useRef('');

  useEffect(() => {
    onTextReceivedRef.current = onTextReceived;
  }, [onTextReceived]);

  const isSupported =
    typeof window !== 'undefined' &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    const SpeechRecognition =
      typeof window === 'undefined'
        ? undefined
        : window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }

    recognitionRef.current = new SpeechRecognition();
    const recognition = recognitionRef.current;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let settled = '';
      // `resultIndex` is where this event's news starts; everything before it
      // has already been handled.
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i];
        if (!result || !result[0]) {
          continue;
        }
        if (result.isFinal) {
          settled += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      if (settled) {
        finalRef.current = `${finalRef.current}${settled}`.replace(/\s+/g, ' ');
        // The caller hears about settled words only. Interim text is for the
        // eye — handing it to a callback that might send it would send a
        // half-heard sentence.
        onTextReceivedRef.current(settled);
      }
      setRecognizedText(`${finalRef.current}${interim}`.trimStart());
    };

    recognition.onerror = (event: SpeechRecognitionError) => {
      // `no-speech` and `aborted` are ordinary: the technician paused, or the
      // session was stopped. Neither is worth surfacing.
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('Speech recognition error on web:', event.error);
      }
    };

    recognition.onend = () => {
      if (!wantListeningRef.current) {
        setIsListening(false);
        return;
      }
      // Ended on its own while the caller still wants it. Start again, and
      // give up rather than loop if the restart itself fails.
      try {
        recognition.start();
      } catch (e) {
        console.warn('Speech recognition could not resume:', e);
        wantListeningRef.current = false;
        setIsListening(false);
      }
    };

    return () => {
      wantListeningRef.current = false;
      try {
        recognition.stop();
      } catch {
        // Already stopped; nothing to do.
      }
    };
  }, []);

  const startListening = useCallback(async () => {
    if (!recognitionRef.current) {
      return;
    }
    finalRef.current = '';
    setRecognizedText('');
    wantListeningRef.current = true;
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (e) {
      // start() throws if a session is already running — which is not a
      // failure, it is the state the caller wanted.
      console.warn('Speech recognition already started:', e);
      setIsListening(true);
    }
  }, []);

  const stopListening = useCallback(async () => {
    wantListeningRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, []);

  /**
   * Forget what has been heard so far, without stopping.
   *
   * For a caller that submits on a pause and then keeps listening: without it
   * the next sentence would arrive with the previous one still glued to its
   * front, because the finals accumulate for the life of the session.
   */
  const reset = useCallback(() => {
    finalRef.current = '';
    setRecognizedText('');
  }, []);

  const toggleListening = useCallback(async () => {
    if (isListening) {
      await stopListening();
    } else {
      await startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    /** Finals plus the interim being revised — what to show while they speak. */
    recognizedText,
    isSupported,
    reset,
    startListening,
    stopListening,
    toggleListening,
    setIsListening,
    permissionStatus: 'granted' as PermissionStatus, // the browser prompts on start()
  };
}
